import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { campanhasQueries } from '../queries';
import { campanhasService } from '../services/campanhasService';
import type { CampanhaInput } from '../types/Campanha';

export function useCampanhas() {
  return useQuery(campanhasQueries.lista());
}

export function useProgressoCampanhas() {
  return useQuery(campanhasQueries.progressoTodas());
}

export function useCampanha(id: string) {
  return useQuery(campanhasQueries.detalhe(id));
}

/** Toda mutação invalida lista e detalhes: o estado efetivo vem sempre da API. */
function useInvalidar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: campanhasQueries.all() });
}

export function useCriarCampanha() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (input: CampanhaInput) => campanhasService.criar(input),
    onSuccess: invalidar,
  });
}

export function useEditarCampanha(id: string) {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (input: Partial<CampanhaInput>) => campanhasService.editar(id, input),
    onSuccess: invalidar,
  });
}

export function useProgresso(id: string) {
  return useQuery(campanhasQueries.progresso(id));
}

/** "Concluir revisão" (spec 34 R5, spec 36 R10): resolve só com o progresso novo no cache, senão o checklist voltaria com o texto pendente. */
export function useMarcarTextoRevisado(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => campanhasService.marcarTextoRevisado(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey }),
  });
}

/** A campanha ativa agora; só busca com o diálogo de Abrir aberto (spec 28 R18). */
export function useCampanhaAtiva(habilitado: boolean) {
  return useQuery({ ...campanhasQueries.ativa(), enabled: habilitado });
}

/** A fase mudou por fora (meia-noite, outra aba): detalhe e progresso refeitos (spec 28). */
export function useInvalidarFase(id: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: campanhasQueries.detalhe(id).queryKey }),
      qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey }),
    ]);
}

export function useAtivarCampanha(id: string) {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (substituir?: string) => campanhasService.ativar(id, substituir),
    onSuccess: invalidar,
  });
}

export function useDesativarCampanha(id: string) {
  const invalidar = useInvalidar();
  return useMutation({ mutationFn: () => campanhasService.desativar(id), onSuccess: invalidar });
}

/** Nome da campanha no login (spec 33 R20); `null` não busca. */
export function useNomePublico(id: string | null) {
  return useQuery({ ...campanhasQueries.nomePublico(id ?? ''), enabled: !!id });
}
